import type { Filters } from '../home.ts'

import type { AppContext } from 'types/common.js'
import component from './WorkOrder.svelte'
import { graphql } from '$houdini'
import { getSession } from 'stores/session'
import { stringToBoolean as toBool } from '@isoftdata/utility-string'
import { datesFromRange, type NamedDateRange } from '@isoftdata/utility-date-time'

export interface WorkOrderFilters extends Filters {
	showClosed: boolean
	showEstimates: boolean
	documentType?: string
}

const workOrderQuery = graphql(`
	query HomeWorkOrders($filter: WorkOrderFilter, $workOrderTypeFilter: WorkOrderTypeFilter) {
		workOrders(filter: $filter) {
			items {
				store {
					id
				}
				workOrderId
				date
				type {
					name
				}
				user {
					name
				}
				total
				totalJobPartQuantity
				totalJobPartQuantityMissing
				totalJobPartQuantityReady
				jobCount
				totalJobsDone
				notes
				description
				datePromised
				closed
				jobs {
					status
				}
			}
		}
		workOrderTypes(filter: $workOrderTypeFilter) {
			items {
				name
				id
			}
		}
	}
`)

const defaults = graphql(`
	query HomeWorkOrderDefaults {
		settingValues {
			home {
				defaultWorkOrderHistoryDateRange
			}
		}
	}
`)

export default function ({ mediator, stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.home.work-order',
		route: 'work-order',
		querystringParameters: ['fromDate', 'toDate', 'userId', 'storeId', 'showClosed', 'showEstimates', 'documentType'],
		defaultParameters: {
			userId: () => getSession()?.userAccountId?.toString() ?? null,
			storeId: () => getSession()?.currentStore?.toString() ?? null,
			showClosed: 'false',
			showEstimates: 'false',
			documentType: '',
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(data, parameters) {
			if (parameters.fromDate === undefined && parameters.toDate === undefined) {
				const res = await defaults.fetch()
				const defaultDateRange = datesFromRange(
					(res?.data?.settingValues?.home?.defaultWorkOrderHistoryDateRange as NamedDateRange) || 'Last 30 Days',
				)

				throw {
					redirectTo: {
						name: null,
						params: {
							fromDate: defaultDateRange.from,
							toDate: defaultDateRange.to,
						},
					},
				}
			}

			const filters: WorkOrderFilters = {
				userId: parameters.userId ? parseInt(parameters.userId, 10) || undefined : undefined,
				storeId: parameters.storeId ? parseInt(parameters.storeId, 10) || undefined : undefined,
				showClosed: toBool(parameters.showClosed),
				showEstimates: toBool(parameters.showEstimates),
				documentType: parameters.documentType,
				dates: {
					from: parameters.fromDate,
					to: parameters.toDate,
				},
			}

			const res = await workOrderQuery.fetch({
				variables: {
					filter: {
						date: {
							gte: filters.dates.from ? new Date(filters.dates.from) : undefined,
							lte: filters.dates.to ? new Date(filters.dates.to) : undefined,
						},
						type: {
							name: filters.documentType ? filters.documentType : undefined,
						},
						isEstimate: filters.showEstimates ? null : false,
						closed: filters.showClosed ? null : false,
						user: {
							id: filters.userId ? [filters.userId] : undefined,
						},
						store: {
							id: filters.storeId ? [filters.storeId] : undefined,
						},
					},
				},
			})

			return {
				workOrders: res.data?.workOrders?.items ?? [],
				documentTypes: res.data?.workOrderTypes.items.map(type => type.name) ?? [],
				...filters,
			}
		},
	})
}
